/**
 * Canonical PDF file naming for applicant-associated documents.
 *
 * Owner directive (2026-10-19): every PDF the portal generates, downloads,
 * exports, or saves for a specific applicant is named
 *
 *   {DocumentName}-{ApplicantID}-{ApplicantName}.pdf
 *
 * e.g. `PlacementDetails-9329833002-AdianCharlton.pdf`. For a PDF generated
 * from a Document Template, the DocumentName is the template name, e.g.
 * `EmploymentAgreement-9329833002-AdianCharlton.pdf`.
 *
 * This module is the single home of that convention. Client components,
 * server.ts, serverDocumentGeneration.ts, and the automation job handlers
 * all import it from here (the server bundle already resolves ./src/lib),
 * so the rule can never drift between runtimes.
 *
 * Contract:
 *  - `applicantId` is the human-facing application number users know (the
 *    custom application id — for application records this is the Firestore
 *    doc id, which IS the displayed number). Callers pass the id of the
 *    record the document belongs to, never ambient state.
 *  - Segments render without spaces: whitespace-separated parts are joined
 *    directly, so "Adian Charlton" renders as "AdianCharlton" and a template
 *    named "Employment Agreement" renders as "EmploymentAgreement".
 *  - Characters that are invalid in file names on Windows/macOS/Linux
 *    (/ \ ? % * : | " < > and control characters) are stripped.
 *  - Non-string/non-number inputs degrade to an empty segment — the result
 *    never contains "undefined", "null", or "[object Object]".
 *  - Missing applicant id or name falls back gracefully: both present →
 *    {Doc}-{Id}-{Name}.pdf; only one present → {Doc}-{that}.pdf; neither →
 *    the descriptive document name alone. No segment is ever invented.
 *  - The result is capped at 180 characters; the DOCUMENT name is truncated
 *    first, never the applicant id, and the result ends in ".pdf" exactly
 *    once (a document name already ending in .pdf does not double it).
 */

const MAX_FILE_NAME_LENGTH = 180;
const PDF_EXTENSION = '.pdf';

// Characters invalid in file names on Windows/macOS/Linux, plus control chars.
const INVALID_FILE_NAME_CHARS = /[\u0000-\u001F\u007F\/\\?%*:|"<>]/g;

/** Coerce an unknown value to a raw string segment, or '' when unusable. */
function rawSegment(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

/** Strip invalid characters and normalise whitespace in one segment. */
function cleanSegment(value: unknown): string {
  return rawSegment(value)
    .replace(INVALID_FILE_NAME_CHARS, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\.+$/, '');
}

/** Join a segment's whitespace-separated parts with no spaces between them. */
function spaceless(value: unknown): string {
  const cleaned = cleanSegment(value);
  if (!cleaned) return '';
  return cleaned.split(' ').filter(Boolean).join('');
}

/** The document-name segment: spaceless, with any trailing .pdf removed. */
function documentNameSegment(value: unknown): string {
  let segment = spaceless(value);
  while (/\.pdf$/i.test(segment)) {
    segment = segment.slice(0, -PDF_EXTENSION.length).replace(/\.+$/, '');
  }
  return segment || 'Document';
}

/**
 * Build the canonical PDF file name for a document.
 *
 * @param documentName  The document's own name (template name for generated
 *                      documents, or a descriptive name such as
 *                      "PlacementDetails" / "Invoice-INV-1001").
 * @param applicantId   The human-facing application number of the applicant
 *                      the document belongs to, when known.
 * @param applicantName The applicant's name as recorded on that same record,
 *                      when known.
 */
export function buildApplicantPdfFileName(
  documentName: unknown,
  applicantId?: unknown,
  applicantName?: unknown,
): string {
  const doc = documentNameSegment(documentName);
  const id = spaceless(applicantId);
  const name = spaceless(applicantName);

  const identityParts = [id, name].filter(Boolean);
  const suffix = identityParts.length > 0 ? `-${identityParts.join('-')}` : '';

  const maxDocLength = MAX_FILE_NAME_LENGTH - PDF_EXTENSION.length - suffix.length;
  let finalDoc = doc;
  if (finalDoc.length > maxDocLength) {
    // Truncate the document name first — never the applicant id. Only if the
    // identity alone would overflow (pathological) is the name tail trimmed.
    finalDoc = finalDoc.slice(0, Math.max(maxDocLength, 0));
    if (finalDoc.length + suffix.length + PDF_EXTENSION.length > MAX_FILE_NAME_LENGTH) {
      const overflow = finalDoc.length + suffix.length + PDF_EXTENSION.length - MAX_FILE_NAME_LENGTH;
      const trimmedName = name.slice(0, Math.max(name.length - overflow, 0));
      const rebuiltSuffix = [id, trimmedName].filter(Boolean).length > 0
        ? `-${[id, trimmedName].filter(Boolean).join('-')}`
        : '';
      return `${finalDoc}${rebuiltSuffix}${PDF_EXTENSION}`;
    }
  }
  return `${finalDoc}${suffix}${PDF_EXTENSION}`;
}
